import office from '@/taskpane/officejs/office-wrapper'
import state from '@/taskpane/state/sidebar-state'
import officeApiWrapper from '@/taskpane/common/office-api-wrapper'
import { EmailListItem } from '../models/email-list-item'
import cirrusApi from '@/taskpane/services/cirrus-api'
import SentItemsQueue, { QueueEntry } from './sent-items-queue'
import emailPostSentMessage from '../messages/email-post-send-message'
import storage from '@/taskpane/common/local-storage'
import configuration from '@/taskpane/common/configuration'
import { logErrorToApi } from '@/taskpane/common/error-logging'

const HOURS_24 = 24 * 60 * 60 * 1000
const POLL_INTERVAL_MS = 30000
const DRAIN_INTERVAL_MS = 5000

export default class SentItemsProcessor {
  private queue = new SentItemsQueue()
  private pollInFlight = false
  private draining = false
  private intervals: ReturnType<typeof setInterval>[] = []
  private storageKey = ''

  private setStorageKey() {
    const mailbox = office.profile.username() || 'unknown'
    this.storageKey = `${configuration.LAST_PROCESSED_SENT_DATETIME_KEY}:${mailbox.toLowerCase()}`
  }

  public start(): void {
    if (this.intervals.length > 0) return

    this.setStorageKey()
    this.queue.hydrate()
    this.pollOnce()
    this.drainOnce()
    this.intervals.push(setInterval(() => this.pollOnce(), POLL_INTERVAL_MS))
    this.intervals.push(setInterval(() => this.drainOnce(), DRAIN_INTERVAL_MS))
  }

  public stop(): void {
    for (const i of this.intervals) clearInterval(i)
    this.intervals = []
  }

  public findById(id: string): QueueEntry | undefined {
    return this.queue.findById(id)
  }

  public ack(id: string): Promise<void> {
    return this.queue.ack(id)
  }

  // Producer: fetch sent items from Graph, match against pendingTracking,
  // enqueue matches for the drain loop to deliver, advance the watermark.
  private async pollOnce(): Promise<void> {
    if (!state.sidebarIsConnected || !state.sidebarIsAuthenticated) return
    if (this.pollInFlight) return
    this.pollInFlight = true

    try {
      const userId = state.getCirrusUserId()
      const storedWatermark = storage.getLocalItem(this.storageKey)
      const lastProcessedDateTime = new Date(storedWatermark || new Date(Date.now() - HOURS_24).toISOString())

      const allItems = officeApiWrapper.isAvailable()
        ? await officeApiWrapper.getSentEmailsToProcess(lastProcessedDateTime)
        : await office.ews.getSentItems(lastProcessedDateTime)

      const pendingTracking = await cirrusApi.getPendingTracking(
        userId,
        allItems.map((item) => ({
          replyTrackingId: item.extendedProperties?.replyTrackingId,
          emailGuid: item.extendedProperties?.emailGuid,
          messageId: item.id,
          threadId: item.threadId || '',
          threadIndex: item.threadIndex || '',
          headerMessageId: item.headerMessageId,
          subject: item.subject,
        })),
      )

      const itemsToEnqueue: EmailListItem[] = []
      for (const item of allItems) {
        try {
          const matchingTracking = pendingTracking.find(
            (tracking) =>
              tracking.messageId === item.id ||
              tracking.headerMessageId === item.headerMessageId ||
              (tracking.threadId === item.threadId && tracking.threadIndex === item.threadIndex),
          )
          if (matchingTracking) {
            itemsToEnqueue.push({
              ...item,
              extendedProperties: {
                ...item.extendedProperties,
                emailGuid: matchingTracking.trackingId,
                replyTrackingId: matchingTracking.replyTrackingId,
              },
            })
          }
        } catch (error) {
          console.error('sent-items-processor: error processing item', { id: item?.id, error })
        }
      }

      if (itemsToEnqueue.length > 0) {
        await this.queue.enqueue(itemsToEnqueue)
      }

      // Advance the watermark based on every item we fetched, not just matched ones.
      // The queue provides durable delivery independent of the watermark, so we can
      // safely move the fetch window forward regardless of downstream ack state.
      let latestSentTime = lastProcessedDateTime
      for (const item of allItems) {
        if (item.sentTime > latestSentTime) {
          latestSentTime = item.sentTime
        }
      }

      const twentyFourHoursAgo = new Date(Date.now() - HOURS_24)
      if (allItems.length === 0 && latestSentTime < twentyFourHoursAgo) {
        latestSentTime = twentyFourHoursAgo
      }

      try {
        storage.setLocalItem(this.storageKey, latestSentTime.toISOString())
      } catch (err) {
        console.error('SentItemsProcessor: localStorage write failed', err)
      }
    } catch (err) {
      // A browser-console message was the only signal the last time this loop failed on every
      // poll, so surface the exception type server-side too.
      console.error('sent-items-processor: poll failed', err)
      logErrorToApi('Sent Items Poll', 'Sent items poll failed', err)
    } finally {
      this.pollInFlight = false
    }
  }

  // Consumer: pull due queue entries, send each, schedule the next attempt so the
  // loop doesn't re-send the same entry every tick while awaiting an ack.
  private async drainOnce(): Promise<void> {
    if (this.draining) return
    if (!state.sidebarIsConnected || !state.sidebarIsAuthenticated) return
    this.draining = true

    try {
      const due = this.queue.peekDueEntries(Date.now())
      for (const entry of due) {
        try {
          emailPostSentMessage({
            headerMessageId: entry.headerMessageId,
            id: entry.id,
            threadId: entry.threadId,
            threadIndex: entry.threadIndex,
            timestamp: new Date(entry.sentTime),
            trackingId: entry.extendedProperties.emailGuid,
            replyTrackingId: entry.extendedProperties.replyTrackingId,
            subject: entry.subject,
            sentBy: entry.sentBy,
            toRecipients: entry.toRecipients,
            ccRecipients: entry.ccRecipients,
            bccRecipients: entry.bccRecipients,
          })
        } catch (error) {
          console.error('sent-items-processor: send threw synchronously', { id: entry.id, error })
        }
        await this.queue.markAttempted(entry.id)
      }
    } catch (err) {
      console.error('sent-items-processor: drain failed', err)
    } finally {
      this.draining = false
    }
  }
}
